import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, LayoutGrid } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import SmartImage from "../components/SmartImage";
import { assetUrl } from "../adminApi";
import LogoSlider from "../components/LogoSlider";
import Seo from "../components/Seo";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { SEO_META } from "../seo";

export default function Portfolio() {
  const [cat, setCat] = useState("All");
  const { t, loc } = useI18n();
  const { projects, categories, content } = useSiteData();
  const navigate = useNavigate();

  const list = useMemo(
    () => (cat === "All" ? projects : projects.filter((p) => p.category === cat)),
    [cat, projects]
  );

  return (
    <div>
      <Seo path="/portfolio" title={loc(SEO_META.portfolio.title)} description={loc(SEO_META.portfolio.description)} />
      <PageHeader
        index={loc(content.pages?.portfolioIndex) || "[ 04 // PORTFOLIO ]"}
        title={loc(content.pages?.portfolioTitle) || t("portfolio.title")}
        lead={loc(content.pages?.portfolioLead) || t("portfolio.lead")}
      />

      {/* navigation shortcuts */}
      <div className="border-b border-[#E2E0D8] bg-[#FAFAFA]">
        <div className="shell flex items-center gap-6 py-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="portfolio-go-back"
            className="mono text-[9.5px] text-[#6B675F] inline-flex items-center gap-2 hover:text-[#141414] transition-colors"
          >
            <ArrowLeft size={12} strokeWidth={1.8} /> {t("cta.goBack")}
          </button>
          <Link
            to="/portfolio"
            data-testid="portfolio-shortcut"
            className="mono text-[9.5px] text-[#141414] inline-flex items-center gap-2 link-underline"
          >
            <LayoutGrid size={12} strokeWidth={1.8} /> {t("nav.portfolio")}
          </Link>
        </div>
      </div>

      <section className="shell py-14 lg:py-20">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="portfolio-filters">
          {categories.map((c) => (
            <button
              key={c.name}
              onClick={() => setCat(c.name)}
              data-testid={`portfolio-filter-${c.name}`}
              className={`mono text-[9.5px] px-4 py-2.5 border transition-colors duration-300 ${
                cat === c.name
                  ? "bg-[#141414] text-[#F7F6F2] border-[#141414]"
                  : "bg-transparent text-[#141414] border-[#E2E0D8] hover:border-[#C8B29B]"
              }`}
            >
              {c.name}
              <span className={`ml-2 ${cat === c.name ? "text-[#C8B29B]" : "text-[#A8A29A]"}`}>{c.count}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {list.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 80}>
              <Link
                to={`/portfolio/${p.slug}`}
                data-testid={`portfolio-card-${p.slug}`}
                className="group block relative border border-[#E2E0D8] hover:border-[#C8B29B] transition-colors duration-500 bg-white"
              >
                <div className="media aspect-[16/11] overflow-hidden">
                  <SmartImage
                    src={assetUrl(p.images?.[0])}
                    alt={loc(p.title)}
                    fallback={p.fallback}
                    fallbackIndex={i}
                    params="w=1600&q=82&auto=format&fit=crop"
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
                  />
                  <span className="absolute top-5 left-5 mono text-[9px] bg-[#F7F6F2]/90 backdrop-blur-sm px-3 py-1.5 text-[#141414]">
                    {p.category}
                  </span>
                </div>
                <div className="p-6 lg:p-8 flex items-start justify-between gap-5">
                  <div className="min-w-0">
                    <h3 className="display text-[24px] lg:text-[32px] text-[#141414] leading-tight">
                      {loc(p.title)}
                    </h3>
                    <p className="mono text-[9.5px] text-[#6B675F] mt-3">
                      {p.location} · {p.year}
                    </p>
                  </div>
                  <span className="mt-1 shrink-0 w-10 h-10 border border-[#E2E0D8] flex items-center justify-center text-[#141414] group-hover:bg-[#141414] group-hover:text-[#F7F6F2] group-hover:border-[#141414] transition-colors duration-400">
                    <ArrowUpRight size={15} strokeWidth={1.6} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {list.length === 0 && (
          <p className="mono text-[10px] text-[#6B675F] py-20 text-center">{t("portfolio.empty")}</p>
        )}
      </section>
      <LogoSlider page="portfolio" anchor="bottom" />
    </div>
  );
}
